import { PureComponent } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import uuid from 'uuid/v4';
import styled from 'styled-components';
import { CircularProgress, InputAdornment, TextField } from '@material-ui/core';
import URI from 'urijs';
import { getSignedUrl as fetchSignedUrl, SignedUrlObject } from 'shared/api/s3';
import { uploadFileWithSignedUrl } from 'shared/util/s3Upload.util';
import { withRouter } from 'react-router-dom';
import config from 'builder/config';
import SiteEditContext from 'shared/util/SiteEditContext';
import { UploadedFile, IconProps } from 'shared/widgets/shared/types';
import { RouteComponentProps } from 'react-router';
import { getTemplateSiteID } from '../../scenes/PageTemplates/utils';
import { defaultTheme } from 'shared/theme/theme';
import { Icon } from 'shared/theme/link-ui-react/Icon';
import { IconType } from 'shared/theme/link-ui-react/types';

export type PathParamsType = {
  siteId: string;
};

export interface S3UploadFileProps extends RouteComponentProps<PathParamsType> {
  id?: string;
  file?: UploadedFile;
  label: string;
  accept: string;
  fileFormatRegex?: RegExp;
  icon?: IconType;
  iconProps?: IconProps;
  onUpload: (file: UploadedFile) => void;
  staticContext: any;
}

const InputWrapper = styled.div`
  & .MuiInputLabel-root,
  .MuiInputBase-root {
    font-family: OptumSans, sans-serif;
  }
  display: flex;
  flex-direction: row;
  width: 100%;
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: auto;
  margin-top: 0.4em;
`;

export const UploadLabel = styled.label`
  &:hover {
    cursor: pointer;
  }
  width: 100%;
`;

const StyledIcon = styled(Icon)`
  fill: ${defaultTheme.colors.aux.blue};
`;

export interface S3UploadFileState {
  uploading: boolean;
  uuid: string;
}

class S3UploadFile extends PureComponent<S3UploadFileProps, S3UploadFileState> {
  public static readonly contextType = SiteEditContext;

  constructor(props: S3UploadFileProps) {
    super(props);
    this.state = {
      uploading: false,
      uuid: uuid(),
    };
  }

  handleFinish = (signedUrlObject: SignedUrlObject) => {
    const { onUpload } = this.props;
    this.setState({
      uploading: false,
    });
    const uri = URI(signedUrlObject.signedUrl);
    const file: UploadedFile = {
      url: uri
        .hostname(config.cdn)
        .query('')
        .toString(),
      name: uri.filename().toString(),
    };
    onUpload(file);
  };

  rejectUpload = () => {
    this.setState({
      uploading: false,
    });
    toast('This file type is not supported', {
      type: 'error',
      theme: 'colored',
    });
  };

  onUploadStart(file: File, callback: any) {
    const fileName = file.name;
    const { fileFormatRegex } = this.props;
    // If extension of file being uploaded is as per fileformatRegex then do callback otherwise reject the upload.
    fileFormatRegex &&
      (fileFormatRegex.test(fileName) ? callback(file) : this.rejectUpload());
  }

  render() {
    const { uploading, uuid } = this.state;
    const {
      // Mark unused props as consumed to satisfy lint rules
      id: _id,
      file,
      label,
      accept,
      icon,
      match: {
        params: { siteId },
        path,
      },
      iconProps,
      staticContext: _staticContext,
    } = this.props;
    const siteUploadId = getTemplateSiteID(siteId, path);
    return (
      <Wrapper>
        <input
          id={uuid}
          type="file"
          style={{ display: 'none' }}
          accept={accept}
          data-test-id={`file-uploader`}
          onChange={async e => {
            const selected = e.target.files?.[0];
            if (!selected) return;
            // Validate file type by filename before starting upload
            if (
              this.props.fileFormatRegex &&
              !this.props.fileFormatRegex.test(selected.name)
            ) {
              toast('Invalid File Type', { type: 'error', theme: 'colored' });
              return;
            }
            this.onUploadStart(selected, async () => {
              try {
                this.setState({ uploading: true });
                const signed = await uploadFileWithSignedUrl(
                  selected,
                  (f, cb) => fetchSignedUrl(f, cb, siteUploadId),
                  {
                    'Content-Type': selected.type || 'application/octet-stream',
                  }
                );
                this.handleFinish(signed);
              } catch (err) {
                this.setState({ uploading: false });
                // Log error for diagnostics while showing user-friendly toast
                // eslint-disable-next-line no-console
                console.error('Error uploading file', err);
                toast('Error uploading file', {
                  type: 'error',
                  theme: 'colored',
                });
              }
            });
          }}
        />
        <InputWrapper>
          <UploadLabel htmlFor={uuid}>
            <TextField
              type="text"
              value={file ? file.name : 'Upload a file'}
              label={label}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {uploading ? (
                      <CircularProgress size={25} />
                    ) : (
                      <StyledIcon
                        icon={icon}
                        width={iconProps?.width}
                        height={iconProps?.height}
                        viewBox={iconProps?.viewBox}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </UploadLabel>
        </InputWrapper>
      </Wrapper>
    );
  }
}

export { S3UploadFile };
export default withRouter(S3UploadFile);
